import { isSuccess } from '@devexperts/remote-data-ts'
import { cn } from '@spiaggeit/spit-ui'

import { ServiceGroupAccordion } from '@/components/ServiceGroupAccordion/ServiceGroupAccordion'
import { useWhiteLabelBgTextClassName } from '@/hooks/useWhiteLabelBgTextClassName.ts'
import { QuoteSetupItems } from '@/models/cart'
import { Service } from '@/models/service'
import { ticketPageGroupMinErrorsSelector } from '@/store/cartSlice/selectors/ticketPageErrorsSelector'
import { dayDifferenceSelector } from '@/store/insertPeriodSlice'
import { ticketSlice } from '@/store/ticketSlice'
import { getGlobalBoughtQuantity } from '@/utils/getGlobalBoughtQuantity'
import { getGroupBoughtQuantity } from '@/utils/getGroupBoughtQuantity'
import { getServiceSingleLimit } from '@/utils/getServiceSingleLimit'
import { useAppSelector } from '../../hooks/store'
import { cartSlice } from '../../store/cartSlice/index'
import { servicesSlice } from '../../store/servicesSlice'
import { Ticket } from './Ticket'

interface Props {
  maximumQuantity: number
  maximumReservationQuantity: number
  maximumSpotQuantity: number
  minimumReservationQuantity: number
  minimumSpotQuantity: number
  reservedQuantity: number
  availableQuantity: number
  hasLimit: boolean
  name: string
  tickets: Service[]
  serviceGroupId: number
  isAccordion?: boolean
  isExpanded?: boolean
  onToggle?: () => void
}

export const Group = (props: Props) => {
  const services = useAppSelector(servicesSlice.selectors.services)
  const cartReservations = useAppSelector(cartSlice.selectors.cartReservations)
  const submitted = useAppSelector(ticketSlice.selectors.submitted)
  const validationScope = useAppSelector(ticketSlice.selectors.validationScope)
  const showReservationErrors = submitted && validationScope === 'reservation'
  const groupMinErrors = useAppSelector(ticketPageGroupMinErrorsSelector)

  const dayDifference = useAppSelector(dayDifferenceSelector)
  const headingClassName = useWhiteLabelBgTextClassName('text-lg font-bold')
  const constraintClassName = useWhiteLabelBgTextClassName(
    'mt-1 text-sm text-neutral-500'
  )

  const ticketDefaults: QuoteSetupItems['boughtServices'] = {}

  if (!isSuccess(services) || props.tickets.length === 0) return

  const hasGroupMinError =
    showReservationErrors &&
    groupMinErrors.some((error) => error.groupId === props.serviceGroupId)

  const firstCartReservation = cartReservations[0] ?? undefined
  Object.keys(firstCartReservation?.boughtServices ?? {}).forEach((key) => {
    const serviceId = Number(key)
    ticketDefaults[serviceId] = firstCartReservation.boughtServices[Number(key)]
  })
  const products = {
    b: 0,
    boughtServices: {
      ...ticketDefaults,
    },
    c: 0,
    d: 0,
    m: 0,
  }

  const ticketsGrid = (
    <div
      className={cn('grid grid-cols-1 items-start gap-4 lg:grid-cols-2', {
        'mt-2': !props.isAccordion,
      })}
    >
      {props.tickets.map((ticket) => {
        const totalGroupBoughtQuantity = getGroupBoughtQuantity({
          groupId: ticket.serviceGroupId,
          selectedElementQuotableProducts: products,
          ticketServices: props.tickets,
        })
        const totalGlobalBoughtQuantity = getGlobalBoughtQuantity(products)
        const availableGlobally = Math.max(
          0,
          Math.min(
            services.value.availableQuantity - totalGlobalBoughtQuantity,
            (services.value.maximumQuantity > 0
              ? services.value.maximumQuantity
              : Infinity) -
              services.value.reservedQuantity -
              totalGlobalBoughtQuantity
          )
        )
        const availableOnGroup = Math.max(
          0,
          Math.min(
            props.availableQuantity - totalGroupBoughtQuantity,
            (props.maximumQuantity > 0 ? props.maximumQuantity : Infinity) -
              props.reservedQuantity -
              totalGroupBoughtQuantity
          )
        )

        return (
          <Ticket
            availability={{
              global: availableGlobally,
              group: availableOnGroup,
              ticket: getServiceSingleLimit({
                dayDifference,
                service: ticket,
              }),
            }}
            key={ticket.serviceId}
            maximumReservationQuantity={props.maximumReservationQuantity}
            maximumSpotQuantity={props.maximumSpotQuantity}
            minimumReservationQuantity={props.minimumReservationQuantity}
            minimumSpotQuantity={props.minimumSpotQuantity}
            ticket={ticket}
          />
        )
      })}
    </div>
  )

  return (
    <div
      className={cn({ 'mb-4': props.isAccordion, 'mb-8': !props.isAccordion })}
    >
      <ServiceGroupAccordion
        constraintClassName={constraintClassName}
        hasError={hasGroupMinError}
        hasReservationMinError={hasGroupMinError}
        isAccordion={props.isAccordion}
        isExpanded={props.isExpanded}
        maximumReservationQuantity={props.maximumReservationQuantity}
        minimumReservationQuantity={props.minimumReservationQuantity}
        name={props.name}
        onToggle={props.onToggle}
        reservationErrorAnchor={`group-${props.serviceGroupId}-reservation`}
        titleClassName={headingClassName}
      >
        {ticketsGrid}
      </ServiceGroupAccordion>
    </div>
  )
}
